/**
 * useOrganizations — data layer for the Organization Management page.
 *
 * Wraps the seven Organization API endpoints in React Query:
 *   - One list query (`getOrganizations`) keyed on filter params so the
 *     pending-only and all-orgs views share the same cache structure but
 *     don't trample each other.
 *   - Four mutations (`create`, `update`, `deactivate`, `approve`) with
 *     optimistic updates against the cached list so admin actions feel
 *     instant. Each rolls back on error and invalidates the list key on
 *     success-or-error to reconcile against the server.
 *
 * Why optimistic over invalidate-only: approve/deactivate are the high-volume
 * actions in the pending queue. A full refetch round-trip per click would
 * read as sluggish even on a fast connection — the queue is meant to feel
 * like clearing an inbox.
 *
 * The hook intentionally exposes the raw `useQuery` / `useMutation` returns
 * for the page so it can drive its own loading/error UI rather than going
 * through an additional state-flattening layer. The pending-count derivation
 * (used by the nav badge) is the one piece of computed state we surface.
 */

import { useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

import { api } from '@/lib/api'
import type {
  CreateOrganizationRequest,
  OrganizationListItemDto,
  PagedOrganizationsResponse,
  UpdateOrganizationRequest,
} from '@/lib/api'

/* -------------------------------------------------------------------------- */
/* Query-key factory                                                          */
/* -------------------------------------------------------------------------- */

/**
 * Keys exported so callers (the nav badge, the page) can invalidate or read
 * the cache from outside the hook. Mirrors the `complianceKeys` factory
 * convention used in `useComplianceData.ts`.
 */
export const orgKeys = {
  all: ['organizations'] as const,
  list: (filters: OrganizationFilters) =>
    [...orgKeys.all, 'list', filters] as const,
  detail: (id: string) =>
    [...orgKeys.all, 'detail', id] as const,
}

/* -------------------------------------------------------------------------- */
/* Public types                                                               */
/* -------------------------------------------------------------------------- */

export interface OrganizationFilters {
  page?: number
  pageSize?: number
  /** Server-side status filter: "All" | "Active" | "Inactive" | "PendingApproval" */
  status?: string
  /** Server-side type filter: "All" | "Customer" | "MSP" | "Platform" */
  type?: string
}

export interface UseOrganizationsResult {
  /** Paged list response — items + total count + page metadata. */
  data?: PagedOrganizationsResponse
  /** True while the initial fetch is in flight. */
  isLoading: boolean
  /** True during a background refetch (after first paint). */
  isFetching: boolean
  error?: Error
  /** Pending-approval count — drives the nav badge and the verdict picker. */
  pendingCount: number
  /** Most-recent pending org name — drives the named-subject verdict form. */
  latestPendingName?: string
  refetch: () => void

  // Mutations — each returns a stable handle so the page can wire its own
  // success/error toasts without re-wrapping the React Query API.
  createOrganization: (data: CreateOrganizationRequest) => Promise<OrganizationListItemDto>
  isCreating: boolean
  updateOrganization: (id: string, data: Partial<UpdateOrganizationRequest>) => Promise<OrganizationListItemDto>
  isUpdating: boolean
  deactivateOrganization: (id: string, reason?: string) => Promise<void>
  isDeactivating: boolean
  rejectOrganization: (id: string, reason?: string) => Promise<void>
  isRejecting: boolean
  approveOrganization: (id: string, opts?: { allowAutoProvision?: boolean }) => Promise<OrganizationListItemDto>
  isApproving: boolean
}

/* -------------------------------------------------------------------------- */
/* Hook                                                                       */
/* -------------------------------------------------------------------------- */

const ORG_STALE_MS = 30 * 1000

export function useOrganizations(filters: OrganizationFilters = {}): UseOrganizationsResult {
  const queryClient = useQueryClient()
  const listKey = orgKeys.list(filters)

  const listQuery = useQuery({
    queryKey: listKey,
    queryFn: () => api.getOrganizations(filters),
    staleTime: ORG_STALE_MS,
  })

  // Pending count + latest pending name drive the nav badge (Layout.tsx)
  // and the OrgBriefAside verdict (`composeOrgBrief`). The derivation is
  // factored into a pure helper so the tests can pin its behavior without
  // standing up a React renderer.
  const { pendingCount, latestPendingName } = useMemo(
    () => derivePendingState(listQuery.data?.items),
    [listQuery.data],
  )

  /* ---------- Mutations ---------- */

  // Optimistic updates use the standard onMutate/onError/onSettled triple
  // documented by TanStack Query. We snapshot the cache, write the optimistic
  // version, roll back on failure, and refetch on settle — so the cache is
  // always reconciled even if our optimistic shape drifts from the server.

  const createMutation = useMutation({
    mutationFn: (data: CreateOrganizationRequest) => api.createOrganization(data),
    onSuccess: () => {
      // Create is non-trivially scoped (depends on filters) — simpler to
      // refetch than to optimistically place the new row in the right page.
      queryClient.invalidateQueries({ queryKey: orgKeys.all })
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<UpdateOrganizationRequest> }) =>
      api.updateOrganization(id, data),
    onMutate: async ({ id, data }) => {
      await queryClient.cancelQueries({ queryKey: listKey })
      const snapshot = queryClient.getQueryData<PagedOrganizationsResponse>(listKey)
      if (snapshot) {
        queryClient.setQueryData<PagedOrganizationsResponse>(listKey, {
          ...snapshot,
          items: snapshot.items.map(o => (o.id === id ? { ...o, ...data } : o)),
        })
      }
      return { snapshot }
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.snapshot) queryClient.setQueryData(listKey, ctx.snapshot)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: orgKeys.all })
    },
  })

  const deactivateMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      api.deactivateOrganization(id, reason),
    onMutate: async ({ id }) => {
      await queryClient.cancelQueries({ queryKey: listKey })
      const snapshot = queryClient.getQueryData<PagedOrganizationsResponse>(listKey)
      if (snapshot) {
        queryClient.setQueryData<PagedOrganizationsResponse>(listKey, {
          ...snapshot,
          items: snapshot.items.map(o =>
            o.id === id ? { ...o, isActive: false, status: 'Inactive' } : o,
          ),
        })
      }
      return { snapshot }
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.snapshot) queryClient.setQueryData(listKey, ctx.snapshot)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: orgKeys.all })
    },
  })

  // Reject mirrors deactivate but commits the honest 'Rejected' status, which
  // the signup recycle path keys on when the same company re-submits.
  const rejectMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      api.rejectOrganization(id, reason),
    onMutate: async ({ id }) => {
      await queryClient.cancelQueries({ queryKey: listKey })
      const snapshot = queryClient.getQueryData<PagedOrganizationsResponse>(listKey)
      if (snapshot) {
        queryClient.setQueryData<PagedOrganizationsResponse>(listKey, {
          ...snapshot,
          items: snapshot.items.map(o =>
            o.id === id ? { ...o, isActive: false, status: 'Rejected' } : o,
          ),
        })
      }
      return { snapshot }
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.snapshot) queryClient.setQueryData(listKey, ctx.snapshot)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: orgKeys.all })
    },
  })

  const approveMutation = useMutation({
    mutationFn: ({ id, opts }: { id: string; opts?: { allowAutoProvision?: boolean } }) =>
      api.approveOrganization(id, opts),
    onMutate: async ({ id }) => {
      await queryClient.cancelQueries({ queryKey: listKey })
      const snapshot = queryClient.getQueryData<PagedOrganizationsResponse>(listKey)
      if (snapshot) {
        queryClient.setQueryData<PagedOrganizationsResponse>(listKey, {
          ...snapshot,
          // Optimistically move the row out of PendingApproval. The list page
          // applies the active-status filter chip on the client side, so the
          // row visibly drops from the pending queue immediately.
          items: snapshot.items.map(o =>
            o.id === id ? { ...o, isActive: true, status: 'Active' } : o,
          ),
        })
      }
      return { snapshot }
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.snapshot) queryClient.setQueryData(listKey, ctx.snapshot)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: orgKeys.all })
    },
  })

  return {
    data: listQuery.data,
    isLoading: listQuery.isLoading,
    isFetching: listQuery.isFetching,
    error: listQuery.error as Error | undefined,
    pendingCount,
    latestPendingName,
    refetch: () => { void listQuery.refetch() },

    createOrganization: (data) => createMutation.mutateAsync(data),
    isCreating: createMutation.isPending,
    updateOrganization: (id, data) => updateMutation.mutateAsync({ id, data }),
    isUpdating: updateMutation.isPending,
    deactivateOrganization: (id, reason) =>
      deactivateMutation.mutateAsync({ id, reason }).then(() => undefined),
    isDeactivating: deactivateMutation.isPending,
    rejectOrganization: (id, reason) =>
      rejectMutation.mutateAsync({ id, reason }).then(() => undefined),
    isRejecting: rejectMutation.isPending,
    approveOrganization: (id, opts) => approveMutation.mutateAsync({ id, opts }),
    isApproving: approveMutation.isPending,
  }
}

/* -------------------------------------------------------------------------- */
/* Pure helpers — exported for unit tests                                     */
/* -------------------------------------------------------------------------- */

/**
 * Compute the pending count + latest pending name from a paged response.
 * Pulled out of the hook so the test file can exercise the derivation
 * logic without invoking React (`@testing-library/react` is not in the
 * project, mirroring the convention in `useComplianceData.test.ts`).
 */
export function derivePendingState(
  items: readonly OrganizationListItemDto[] | undefined,
): { pendingCount: number; latestPendingName?: string } {
  if (!items || items.length === 0) return { pendingCount: 0 }
  const pending = items.filter(o => o.status === 'PendingApproval')
  if (pending.length === 0) return { pendingCount: 0 }
  const latest = [...pending].sort((a, b) => {
    const aT = Date.parse(a.createdAt) || 0
    const bT = Date.parse(b.createdAt) || 0
    return bT - aT
  })[0]
  return {
    pendingCount: pending.length,
    latestPendingName: latest?.name,
  }
}

/**
 * Optimistic-update logic for approve — pure function so the test file can
 * verify the row transition without driving the mutation pipeline.
 */
export function applyOptimisticApprove(
  snapshot: PagedOrganizationsResponse | undefined,
  id: string,
): PagedOrganizationsResponse | undefined {
  if (!snapshot) return snapshot
  return {
    ...snapshot,
    items: snapshot.items.map(o =>
      o.id === id ? { ...o, isActive: true, status: 'Active' } : o,
    ),
  }
}

/**
 * Optimistic-update logic for deactivate — pure function counterpart to
 * `applyOptimisticApprove`. Same testability rationale.
 */
export function applyOptimisticDeactivate(
  snapshot: PagedOrganizationsResponse | undefined,
  id: string,
): PagedOrganizationsResponse | undefined {
  if (!snapshot) return snapshot
  return {
    ...snapshot,
    items: snapshot.items.map(o =>
      o.id === id ? { ...o, isActive: false, status: 'Inactive' } : o,
    ),
  }
}
